import React, { useState, useEffect } from 'react';
// import { useGeolocation } from '../hooks/useGeolocation';
import { MapPin, Search, Compass } from 'lucide-react';

const useGeolocation = (options = {}) => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setLoading(false);
      return;
    }

    const handleSuccess = (position) => {
      setLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      });
      setLoading(false);
      setError(null);
    };

    const handleError = (error) => {
      setError(error.message);
      setLoading(false);
    };

    const watchId = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
        ...options,
      }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, [options]);

  return { location, error, loading };
};

const LocationSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const { location, error: locationError } = useGeolocation();

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const searchPlaces = async () => {
    setLoading(true);
    try {
      // Here you would integrate with your chosen location API
      // Example using coordinates or search query
      const coordinates = location
        ? `${location.latitude},${location.longitude}`
        : '';

      const response = await fetch(
        `/api/places/search?q=${searchQuery}&location=${coordinates}`
      );
      const data = await response.json();
      setNearbyPlaces(data.places);
    } catch (error) {
      console.error('Error searching places:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='max-w-4xl mx-auto p-4'>
      <div className='mb-6'>
        <div className='relative'>
          <input
            type='text'
            placeholder='Search locations...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className='w-full p-4 pl-12 pr-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          />
          <Search className='absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400' />
          {location && (
            <button
              onClick={() => searchPlaces()}
              className='absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-500 hover:text-blue-600'
            >
              <Compass className='h-5 w-5' />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className='flex justify-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
        </div>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {nearbyPlaces.map((place) => (
            <div
              key={place.id}
              className='bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow duration-200'
            >
              {place.verified && (
                <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                  Verified
                </span>
              )}
              <h3 className='text-lg font-semibold mt-2'>{place.name}</h3>
              <p className='text-gray-600 text-sm mt-1'>{place.address}</p>
              <div className='flex items-center mt-2 text-sm text-gray-500'>
                <MapPin className='h-4 w-4 mr-1' />
                {place.distance} km away
              </div>
              <button
                onClick={() => handleLocationSelect(place)}
                className='mt-3 w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200'
              >
                View Details
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationSearch;

// import { useState, useEffect } from 'react';
